import type { SetupRow, SetupStepId } from '@shared/setup'
import type { LocalSetupState } from '../data/setup-state'

/**
 * Pure setup decisions: whether setup shows on its own, in which form, and
 * which steps a walk visits. No I/O; the host gathers the inputs.
 */

/** Back in the app after at least this long away counts as an app open. */
export const SETUP_RESUME_AFTER_MS = 15 * 60 * 1000

/** A required step blocked in Settings and put off this many times is reminded with a slim banner. */
export const BANNER_AFTER_DISMISSALS = 3

export type SetupMode = 'first-run' | 'reminder' | 'manual' | 'deep-link'

export type AutoSetupDecision = 'first-run' | 'reminder' | 'banner' | null

export function shouldPromptOnResume(hiddenAtMs: number | null, nowMs: number): boolean {
  return hiddenAtMs != null && nowMs - hiddenAtMs >= SETUP_RESUME_AFTER_MS
}

const isOpen = (row: SetupRow) => row.state === 'todo' || row.state === 'blocked'

/** Required steps not done on this device (a failed check is not counted). */
export function outstandingRequired(rows: readonly SetupRow[]): SetupRow[] {
  return rows.filter((r) => r.level === 'required' && isOpen(r))
}

/**
 * A required step shows as a popup until it's put off: once if the phone never
 * asked (or it isn't a phone setting), 3 times if it's blocked in Settings.
 * After that the slim banner reminds instead.
 */
export function remindsWithBanner(row: SetupRow, local: LocalSetupState): boolean {
  const putOff = local.dismissals[row.id] ?? 0
  return putOff >= (row.state === 'blocked' ? BANNER_AFTER_DISMISSALS : 1)
}

export type ConsentRequestReminder = 'none' | 'popup' | 'banner'

/** An admin's consent request shows as a popup once, then the banner reminds until it's answered. */
export function consentRequestReminder(input: {
  consentRequestPending: boolean
  consentRequestedAt: string | null
  local: LocalSetupState
}): ConsentRequestReminder {
  if (!input.consentRequestPending) return 'none'
  return input.consentRequestedAt != null && input.local.consentRequestShownFor === input.consentRequestedAt
    ? 'banner'
    : 'popup'
}

/** Required steps still open here (and a pending consent request), split by how an app open reminds. */
export function planReminders(input: {
  rows: readonly SetupRow[]
  local: LocalSetupState
  consentRequest: ConsentRequestReminder
  /** Steps an admin asked about again and this device hasn't shown yet: popup once, whatever their level. */
  adminSteps?: readonly SetupStepId[]
}): { popup: SetupStepId[]; banner: SetupStepId[] } {
  const popup: SetupStepId[] = []
  const banner: SetupStepId[] = []
  for (const row of input.rows) {
    if (row.id === 'recording-consent' && input.consentRequest !== 'none') {
      if (input.consentRequest === 'popup') popup.push(row.id)
      else banner.push(row.id)
    } else if (input.adminSteps?.includes(row.id) && row.state !== 'done') {
      popup.push(row.id)
    } else if (row.level === 'required' && isOpen(row)) {
      if (remindsWithBanner(row, input.local)) banner.push(row.id)
      else popup.push(row.id)
    }
  }
  return { popup, banner }
}

/**
 * What an app open shows:
 * - first run owed (never finished here, older flow, or admin reset) → the full flow;
 * - a required step (or an admin consent request) not yet put off → the reminder card, for those;
 * - only steps already put off → a slim banner;
 * - otherwise nothing.
 */
export function decideAutoSetup(input: {
  rows: readonly SetupRow[]
  local: LocalSetupState
  firstRunOwed: boolean
  consentRequest: ConsentRequestReminder
  adminSteps?: readonly SetupStepId[]
}): AutoSetupDecision {
  if (input.firstRunOwed) return 'first-run'
  const plan = planReminders(input)
  if (plan.popup.length > 0) return 'reminder'
  if (plan.banner.length > 0) return 'banner'
  return null
}

/**
 * The steps a walk visits, in setup order.
 * - first run: required not done, optional not done and not yet asked, info not read
 *   (a reset run asks optional steps and explainers again);
 * - reminder: required not done (plus the call-recording step when an admin asked again);
 * - manual: everything not done, including optional steps asked before;
 * - deep link: that one step (if it applies), or the first-run walk for `overview`.
 */
export function stepsToWalk(input: {
  mode: SetupMode
  rows: readonly SetupRow[]
  local: LocalSetupState
  resetRun?: boolean
  consentRequestPending?: boolean
  /** Steps an admin asked about again: a reminder walk visits them whatever their level. */
  adminSteps?: readonly SetupStepId[]
  startAt?: SetupStepId | 'overview'
}): SetupStepId[] {
  const { rows, local } = input
  const notDone = (r: SetupRow) => r.state !== 'done'
  const consentAgain = (r: SetupRow) => r.id === 'recording-consent' && input.consentRequestPending === true
  const adminAsked = (r: SetupRow) => input.adminSteps?.includes(r.id) === true && r.state !== 'done'
  switch (input.mode) {
    case 'reminder':
      return rows
        .filter((r) => (r.level === 'required' && isOpen(r)) || consentAgain(r) || adminAsked(r))
        .map((r) => r.id)
    case 'manual':
      return rows.filter((r) => notDone(r) || consentAgain(r)).map((r) => r.id)
    case 'deep-link':
      if (input.startAt && input.startAt !== 'overview') {
        return rows.some((r) => r.id === input.startAt) ? [input.startAt] : []
      }
      return firstRunSteps(rows, local, input.resetRun === true, input.consentRequestPending === true)
    case 'first-run':
      return firstRunSteps(rows, local, input.resetRun === true, input.consentRequestPending === true)
  }
}

function firstRunSteps(
  rows: readonly SetupRow[],
  local: LocalSetupState,
  resetRun: boolean,
  consentRequestPending: boolean,
): SetupStepId[] {
  const asked = new Set(local.optionalAsked)
  return rows
    .filter((r) => {
      if (r.id === 'recording-consent' && consentRequestPending) return true
      if (r.level === 'info') return resetRun || r.state !== 'done'
      if (r.state === 'done') return false
      if (r.level === 'optional') return resetRun || !asked.has(r.id)
      return true
    })
    .map((r) => r.id)
}

export type OverviewVariant = 'new' | 'some-done' | 'all-done'

/** All done = nothing left to walk; new = nothing done yet on this device. */
export function overviewVariant(rows: readonly SetupRow[], walk: readonly SetupStepId[]): OverviewVariant {
  if (walk.length === 0) return 'all-done'
  return rows.some((r) => r.state === 'done') ? 'some-done' : 'new'
}
